import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Container as ContainerBase } from "components/misc/Layouts.js";
import { Typography, Paper, Divider } from "@mui/material";
import { motion } from "framer-motion";
import BgTitle from "components/title/BgTitle";
import { useLocation } from "react-router-dom";

// Styled Components
const Container = tw(ContainerBase)`relative py-10 md:py-16`;
const ContentWrapper = styled(motion.div)`
  ${tw`max-w-6xl mx-auto`}
`;

const StyledPaper = styled(Paper)`
  ${tw`p-0 mt-6 rounded-lg shadow-lg overflow-hidden`}
  border-left: ${({ isImportant }) =>
    isImportant ? "2px solid #FF0000" : "2px solid #0c4d96"};
`;

const HeaderSection = styled.div`
  ${tw`p-6 md:p-8 bg-white`}
`;

const ContentSection = styled.div`
  ${tw`p-6 md:p-8 bg-gray-100`}
  background-color: #f8f9fa;
`;

const DateText = styled.div`
  ${tw`text-sm mb-2 flex items-center`}
  color: ${({ isImportant }) => (isImportant ? "#FF0000" : "#6B7280")};
  font-weight: ${({ isImportant }) => (isImportant ? "600" : "normal")};

  &:before {
    content: "";
    display: inline-block;
    width: 10px;
    height: 10px;
    background-color: ${({ isImportant }) =>
      isImportant ? "#FF0000" : "#0c4d96"};
    ${tw`mr-2 rounded-full`}
  }
`;

const AnnouncementContent = styled.div`
  ${tw`leading-relaxed whitespace-pre-line`}
  font-size: 1rem;
  line-height: 1.8;
`;

// Helper to format paragraph spacing
const FormattedContent = ({ content }) => {
  const paragraphs = content.split("\n");
  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <Typography
          key={index}
          variant="body1"
          paragraph={index < paragraphs.length - 1}
          sx={{
            lineHeight: 1.8,
            fontSize: { xs: "0.95rem", md: "1rem" },
            color: "#333",
            marginBottom: paragraph === "" ? "1rem" : "0rem",
          }}
        >
          {paragraph}
        </Typography>
      ))}
    </>
  );
};

const Announcement = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Determine which announcement to show based on path
  const isWebsiteRenewal = location.pathname.includes(
    "/announcement/website-renewal"
  );
  const isFraudAlert = location.pathname.includes(
    "/announcement/notice-fraud-alert"
  );
  const isFraudulentEmails = location.pathname.includes(
    "/announcement/fraudulent-emails-impersonating-our-company"
  );

  // Key from route that matches localization keys
  const contentKey = isWebsiteRenewal
    ? "website-renewal"
    : isFraudAlert
    ? "notice-fraud-alert"
    : isFraudulentEmails
    ? "fraudulent-emails-impersonating-our-company"
    : "website-renewal"; // fallback/default

  const isImportant =
    contentKey === "notice-fraud-alert" ||
    contentKey === "fraudulent-emails-impersonating-our-company";

  // Animation config
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <Container>
      <ContentWrapper
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header Title */}
        <motion.div variants={itemVariants}>
          <BgTitle text={t("announcement.heading")} />
        </motion.div>

        {/* Main Paper Card */}
        <motion.div variants={itemVariants}>
          <StyledPaper elevation={3} isImportant={isImportant}>
            {/* Header Info */}
            <HeaderSection>
              <DateText isImportant={isImportant}>
                {t(`announcement.${contentKey}.date`)}
              </DateText>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: "600",
                  color: isImportant ? "#FF0000" : "#0c4d96",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                }}
              >
                {t(`announcement.${contentKey}.title`)}
              </Typography>
            </HeaderSection>

            <Divider />

            {/* Main Content */}
            <ContentSection>
              <AnnouncementContent>
                <FormattedContent
                  content={t(`announcement.${contentKey}.content`)}
                />
              </AnnouncementContent>
            </ContentSection>
          </StyledPaper>
        </motion.div>
      </ContentWrapper>
    </Container>
  );
};

export default Announcement;
